import {
  HAIRSTYLE_CONFIGS,
  getHairstyleConfig,
  type HairstyleConfig,
} from '../config/hairstyles';
import type { HairQuizVariant } from '../types';

const QUIZ_RESULTS_COUNT = 3;

const getVariantStyleId = (variant: HairQuizVariant): string => variant.style_id || variant.id || '';

const createCatalogVariant = (style: HairstyleConfig): HairQuizVariant => ({
  id: style.id,
  style_id: style.id,
  title: style.name.ru,
  description: style.description.ru,
  image_prompt_en: style.promptHint,
});

const similarityScore = (candidate: HairstyleConfig, selected: HairstyleConfig[]): number => {
  if (!selected.length) return 0;

  return selected.reduce((score, style) => {
    if (candidate.length && candidate.length === style.length) score += 4;
    if (candidate.vibe && candidate.vibe === style.vibe) score += 2;
    return score;
  }, 0);
};

export const normalizeQuizRecommendations = (variants: HairQuizVariant[]): HairQuizVariant[] => {
  const uniqueVariants: HairQuizVariant[] = [];
  const selectedIds = new Set<string>();

  for (const variant of variants) {
    const style = getHairstyleConfig(getVariantStyleId(variant));
    if (!style || selectedIds.has(style.id)) continue;

    selectedIds.add(style.id);
    uniqueVariants.push({ ...variant, id: style.id, style_id: style.id });
    if (uniqueVariants.length === QUIZ_RESULTS_COUNT) return uniqueVariants;
  }

  const selectedStyles = uniqueVariants
    .map((variant) => getHairstyleConfig(getVariantStyleId(variant)))
    .filter((style): style is HairstyleConfig => Boolean(style));

  const complementaryStyles = HAIRSTYLE_CONFIGS
    .filter((style) => !style.hiddenInCatalog && !selectedIds.has(style.id))
    .map((style, catalogIndex) => ({
      style,
      catalogIndex,
      score: similarityScore(style, selectedStyles),
    }))
    .sort((left, right) => right.score - left.score || left.catalogIndex - right.catalogIndex);

  for (const { style } of complementaryStyles) {
    uniqueVariants.push(createCatalogVariant(style));
    if (uniqueVariants.length === QUIZ_RESULTS_COUNT) break;
  }

  return uniqueVariants.slice(0, QUIZ_RESULTS_COUNT);
};
